import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { updateCalendarEventFull } from "@/lib/calendarApi";
import { type CalEvent, SHOW_AS_OPTIONS, toDateInput, toTimeInput } from "./types";

/** Extracted verbatim from client/src/pages/Calendar.tsx — no behavior change. */
export function EditEventModal({ event, onClose, onSaved }: { event: CalEvent; onClose: () => void; onSaved: () => void }) {
  const { toast } = useToast();
  const [subject, setSubject] = useState(event.subject ?? "");
  const [date, setDate] = useState(toDateInput(event.start.dateTime));
  const [startTime, setStartTime] = useState(toTimeInput(event.start.dateTime));
  const [endTime, setEndTime] = useState(toTimeInput(event.end.dateTime));
  const [isAllDay, setIsAllDay] = useState(event.isAllDay);
  const [showAs, setShowAs] = useState<"free" | "busy" | "tentative" | "oof" | "workingElsewhere">(
    (event.showAs as any) ?? "busy",
  );

  const isRecurringInstance = event.type === "occurrence" || event.type === "exception" || event.type === "seriesMaster";

  const mutation = useMutation({
    mutationFn: () =>
      updateCalendarEventFull(event.id, {
        subject: subject.trim(),
        date,
        startTime: isAllDay ? undefined : startTime,
        endTime: isAllDay ? undefined : endTime,
        isAllDay,
        showAs,
      }),
    onSuccess: () => {
      toast({ title: "Event updated" });
      onSaved();
      onClose();
    },
    onError: (err: Error) => toast({ title: "Failed to update event", description: err.message, variant: "destructive" }),
  });

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Edit Event</DialogTitle>
        </DialogHeader>
        {isRecurringInstance && (
          <p className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
            This is part of a recurring series. Editing a single occurrence's time/details isn't
            supported yet — delete this occurrence (or the series) instead, or edit the event
            directly in Outlook.
          </p>
        )}
        <div className="space-y-3 py-1">
          <div className="space-y-1.5">
            <Label htmlFor="edit-subject">Title</Label>
            <Input id="edit-subject" value={subject} onChange={(e) => setSubject(e.target.value)} autoFocus />
          </div>
          <div className="flex items-center gap-2">
            <input
              id="edit-allday"
              type="checkbox"
              checked={isAllDay}
              onChange={(e) => setIsAllDay(e.target.checked)}
              className="h-4 w-4 rounded border-gray-300"
            />
            <Label htmlFor="edit-allday" className="cursor-pointer">All day</Label>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="edit-date">Date</Label>
            <Input id="edit-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
          </div>
          {!isAllDay && (
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label htmlFor="edit-start">Start (ET)</Label>
                <Input id="edit-start" type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="edit-end">End (ET)</Label>
                <Input id="edit-end" type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} />
              </div>
            </div>
          )}
          <div className="space-y-1.5">
            <Label>Show as</Label>
            <Select value={showAs} onValueChange={(v) => setShowAs(v as typeof showAs)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SHOW_AS_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button disabled={!subject.trim() || !date || mutation.isPending} onClick={() => mutation.mutate()}>
            {mutation.isPending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
